import { useState } from 'react';
import { useCheckIOC, useThreats, useCVEInfo, useRunScan } from '../api/analysis';
import type { IOCResult, ThreatEntry, ScanResult } from '../types';

type ActiveTab = 'ioc' | 'feeds' | 'vulnerabilities' | 'exposure';

export default function CyberIntel() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('ioc');

  const tabs: { id: ActiveTab; label: string; icon: React.ReactNode }[] = [
    {
      id: 'ioc',
      label: 'IOC Checker',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
      ),
    },
    {
      id: 'feeds',
      label: 'Threat Intel',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12.75 19.5v-.75a7.5 7.5 0 00-7.5-7.5H4.5m0-6.75h.75c7.87 0 14.25 6.38 14.25 14.25v.75M6 18.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
        </svg>
      ),
    },
    {
      id: 'vulnerabilities',
      label: 'CVE Lookup',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0-10.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.75c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
        </svg>
      ),
    },
    {
      id: 'exposure',
      label: 'Security Scan',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5" />
        </svg>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Cyber Intelligence</h1>
        <p className="text-sm text-gray-500 mt-1">
          Threat intelligence, IOC analysis, and vulnerability management
        </p>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-2 border-b border-border-subtle pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === tab.id
                ? 'text-cyber-cyan border-b-2 border-cyber-cyan bg-surface-tertiary/50'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'ioc' && <IOCChecker />}
      {activeTab === 'feeds' && <ThreatIntelPanel />}
      {activeTab === 'vulnerabilities' && <CVELookup />}
      {activeTab === 'exposure' && <SecurityScanPanel />}
    </div>
  );
}

// ── IOC Checker ──
function IOCChecker() {
  const [indicator, setIndicator] = useState('');
  const [result, setResult] = useState<IOCResult | null>(null);
  const checkMut = useCheckIOC();

  function handleCheck() {
    if (!indicator.trim()) return;
    checkMut.mutate(indicator.trim(), {
      onSuccess: (data) => setResult(data),
    });
  }

  function getSeverityClass(severity: string) {
    switch (severity) {
      case 'critical': return 'bg-red-500/10 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/10 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-amber-500/10 text-amber-400 border-amber-500/30';
      case 'low': return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/30';
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="glass-panel p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-300">Check Indicator of Compromise</h3>
        <p className="text-xs text-gray-500">
          Enter an IP address, domain, URL, file hash, email, or CVE ID. The type is auto-detected.
        </p>

        <div className="flex gap-2">
          <input
            type="text"
            value={indicator}
            onChange={(e) => setIndicator(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
            placeholder="e.g., 192.168.1.1, evil.com, CVE-2024-1234"
            className="flex-1 px-4 py-2.5 bg-surface-primary border border-border-subtle rounded-lg text-sm text-gray-200 placeholder-gray-600 font-mono focus:outline-none focus:border-cyber-cyan/40"
          />
          <button
            onClick={handleCheck}
            disabled={!indicator.trim() || checkMut.isPending}
            className="px-6 py-2.5 text-sm font-medium bg-cyber-cyan/10 text-cyber-cyan border border-cyber-cyan/30 rounded-lg hover:bg-cyber-cyan/20 transition-colors disabled:opacity-40"
          >
            {checkMut.isPending ? 'Checking...' : 'Check'}
          </button>
        </div>

        {checkMut.error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
            IOC check failed: {(checkMut.error as Error).message || 'Server error'}
          </div>
        )}
      </div>

      {result && (
        <div className="glass-panel p-6 space-y-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-300">Result</h3>
            <div className="flex items-center gap-2">
              <span className={`entity-badge border ${getSeverityClass(result.severity)}`}>
                {result.severity.toUpperCase()}
              </span>
              <span
                className={`entity-badge border ${
                  result.is_malicious
                    ? 'bg-red-500/10 text-red-400 border-red-500/30'
                    : 'bg-green-500/10 text-green-400 border-green-500/30'
                }`}
              >
                {result.is_malicious ? 'MALICIOUS' : 'CLEAN'}
              </span>
            </div>
          </div>

          <div className="space-y-3 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Indicator</span>
              <span className="text-gray-200 font-mono">{result.indicator}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Type</span>
              <span className="text-gray-300">{result.indicator_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Confidence</span>
              <span className={`font-mono font-medium ${result.confidence > 0.7 ? 'text-red-400' : result.confidence > 0.4 ? 'text-amber-400' : 'text-green-400'}`}>
                {Math.round(result.confidence * 100)}%
              </span>
            </div>
            {result.first_seen && (
              <div className="flex justify-between">
                <span className="text-gray-500">First Seen</span>
                <span className="text-gray-300">{new Date(result.first_seen).toLocaleDateString()}</span>
              </div>
            )}
            {result.last_seen && (
              <div className="flex justify-between">
                <span className="text-gray-500">Last Seen</span>
                <span className="text-gray-300">{new Date(result.last_seen).toLocaleDateString()}</span>
              </div>
            )}
          </div>

          {result.sources.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Sources ({result.sources.length})</p>
              <div className="flex flex-wrap gap-1">
                {result.sources.map((src, i) => (
                  <span key={i} className="px-2 py-0.5 text-xs bg-surface-tertiary text-gray-400 rounded-full">
                    {typeof src === 'string' ? src : String((src as Record<string, unknown>).name || JSON.stringify(src))}
                  </span>
                ))}
              </div>
            </div>
          )}

          {result.tags.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Tags</p>
              <div className="flex flex-wrap gap-1">
                {result.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 text-xs bg-red-500/10 text-red-400 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {result.related_indicators.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Related Indicators</p>
              <div className="flex flex-wrap gap-1">
                {result.related_indicators.map((ri) => (
                  <span key={ri} className="px-2 py-0.5 text-xs bg-surface-tertiary text-gray-400 rounded-full font-mono">
                    {ri}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Threat Intel ──
function ThreatIntelPanel() {
  const [days, setDays] = useState(7);
  const { data: feedResponse, isLoading, error } = useThreats({ days });

  const items = feedResponse?.items || [];

  function getSeverityClass(severity: string) {
    switch (severity) {
      case 'critical': return 'bg-red-500/10 text-red-400';
      case 'high': return 'bg-orange-500/10 text-orange-400';
      case 'medium': return 'bg-amber-500/10 text-amber-400';
      case 'low': return 'bg-blue-500/10 text-blue-400';
      default: return 'bg-gray-500/10 text-gray-400';
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          {feedResponse ? `${feedResponse.total} threats found` : 'Loading...'}
        </p>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="text-xs bg-surface-tertiary border border-border-subtle rounded px-2 py-1 text-gray-300"
        >
          <option value={1}>Last 24h</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
          <span className="ml-2 text-sm text-gray-500">Loading threats...</span>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
          Failed to load threat intelligence.
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="text-center py-12 text-sm text-gray-500">
          No threats found for this period. The Elasticsearch index may not have data yet.
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-3">
          {items.map((threat: ThreatEntry) => (
            <div key={threat.id} className="glass-panel p-4 space-y-2">
              <div className="flex items-center gap-3">
                <span className={`entity-badge ${getSeverityClass(threat.severity)}`}>
                  {threat.severity.toUpperCase()}
                </span>
                <span className="text-sm font-medium text-gray-200 flex-1">{threat.title}</span>
                <span className="text-xs text-gray-500">{threat.source}</span>
              </div>
              <p className="text-sm text-gray-400">{threat.description}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-500">Type: {threat.threat_type}</span>
                {threat.indicators.length > 0 && (
                  <span className="text-xs text-gray-500">
                    | {threat.indicators.length} indicator{threat.indicators.length > 1 ? 's' : ''}
                  </span>
                )}
                <span className="text-xs text-gray-600 ml-auto">
                  {new Date(threat.published_at).toLocaleDateString()}
                </span>
              </div>
              {threat.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {threat.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 text-xs bg-surface-tertiary text-gray-400 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── CVE Lookup ──
function CVELookup() {
  const [cveId, setCveId] = useState('');
  const [searchId, setSearchId] = useState<string | null>(null);
  const { data: cve, isLoading, error } = useCVEInfo(searchId);

  function handleSearch() {
    const v = cveId.trim().toUpperCase();
    if (!v.startsWith('CVE-')) return;
    setSearchId(v);
  }

  function getSeverityClass(severity: string) {
    switch (severity) {
      case 'critical': return 'bg-red-500/10 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/10 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-amber-500/10 text-amber-400 border-amber-500/30';
      case 'low': return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/30';
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input
          type="text"
          value={cveId}
          onChange={(e) => setCveId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Enter CVE ID, e.g. CVE-2024-12345"
          className="flex-1 px-4 py-2.5 bg-surface-tertiary border border-border-subtle rounded-lg text-sm text-gray-200 placeholder-gray-600 font-mono focus:outline-none focus:border-cyber-cyan/40"
        />
        <button
          onClick={handleSearch}
          disabled={!cveId.trim().toUpperCase().startsWith('CVE-') || isLoading}
          className="px-6 py-2.5 text-sm font-medium bg-cyber-cyan/10 text-cyber-cyan border border-cyber-cyan/30 rounded-lg hover:bg-cyber-cyan/20 transition-colors disabled:opacity-40"
        >
          {isLoading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {error && (
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm text-amber-400">
          CVE not found. Make sure the format is correct (CVE-YYYY-NNNNN) and the CVE exists in the database.
        </div>
      )}

      {cve && (
        <div className="glass-panel p-6 space-y-4 animate-fade-in">
          <div className="flex items-center gap-3">
            <span className="text-lg font-mono font-semibold text-cyber-cyan">{cve.cve_id}</span>
            <span className={`entity-badge border ${getSeverityClass(cve.severity)}`}>
              {cve.severity.toUpperCase()}
            </span>
            {cve.cvss_score != null && (
              <span className="ml-auto text-sm font-mono text-gray-400">
                CVSS {cve.cvss_score.toFixed(1)}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-300">{cve.description}</p>

          {cve.exploits_available && (
            <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400 font-medium">
              Exploits available in the wild
            </div>
          )}

          {cve.affected_products.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Affected Products</p>
              <div className="flex flex-wrap gap-1">
                {cve.affected_products.map((p) => (
                  <span key={p} className="px-2 py-0.5 text-xs bg-surface-tertiary text-gray-400 rounded-full">
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-4 text-xs text-gray-500">
            {cve.published_at && (
              <span>Published: {new Date(cve.published_at).toLocaleDateString()}</span>
            )}
            {cve.last_modified && (
              <span>Modified: {new Date(cve.last_modified).toLocaleDateString()}</span>
            )}
          </div>

          {cve.references.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">References</p>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {cve.references.map((ref, i) => (
                  <div key={i} className="text-xs text-cyber-cyan/70 font-mono truncate">{ref}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!cve && !error && !isLoading && searchId && (
        <p className="text-sm text-gray-500 text-center py-8">No results</p>
      )}
    </div>
  );
}

// ── Security Scan ──
function SecurityScanPanel() {
  const [target, setTarget] = useState('');
  const scanMut = useRunScan();

  function handleScan() {
    if (!target.trim()) return;
    scanMut.mutate({ target: target.trim() });
  }

  const result: ScanResult | undefined = scanMut.data;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="glass-panel p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-300">Run Security Scan</h3>
        <p className="text-xs text-gray-500">
          Scan a domain or IP address for DNS records, open ports, SSL info, and security issues.
        </p>

        <div className="flex gap-2">
          <input
            type="text"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleScan()}
            placeholder="e.g., example.com or 203.0.113.10"
            className="flex-1 px-4 py-2.5 bg-surface-primary border border-border-subtle rounded-lg text-sm text-gray-200 placeholder-gray-600 font-mono focus:outline-none focus:border-cyber-cyan/40"
          />
          <button
            onClick={handleScan}
            disabled={!target.trim() || scanMut.isPending}
            className="px-6 py-2.5 text-sm font-medium bg-cyber-cyan/10 text-cyber-cyan border border-cyber-cyan/30 rounded-lg hover:bg-cyber-cyan/20 transition-colors disabled:opacity-40"
          >
            {scanMut.isPending ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
                Scanning...
              </span>
            ) : (
              'Scan'
            )}
          </button>
        </div>

        {scanMut.error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
            Scan failed: {(scanMut.error as Error).message || 'Server error'}
          </div>
        )}
      </div>

      {result && (
        <div className="glass-panel p-6 space-y-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-300">Scan Results</h3>
            <span className="text-xs text-gray-500 font-mono">{result.target}</span>
          </div>

          <div className="flex items-center gap-2 text-xs flex-wrap">
            <span className="text-gray-500">Status:</span>
            <span className={`font-medium ${result.status === 'completed' ? 'text-green-400' : 'text-amber-400'}`}>
              {result.status.toUpperCase()}
            </span>
            <span className="text-gray-500 ml-4">Type:</span>
            <span className="text-gray-300">{result.scan_type}</span>
            {result.risk_score != null && (
              <>
                <span className="text-gray-500 ml-4">Risk:</span>
                <span className={`font-mono font-medium ${result.risk_score > 70 ? 'text-red-400' : result.risk_score > 40 ? 'text-amber-400' : 'text-green-400'}`}>
                  {result.risk_score}
                </span>
              </>
            )}
          </div>

          {result.dns_records.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">DNS Records ({result.dns_records.length})</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {result.dns_records.map((rec, i) => (
                  <div key={i} className="flex gap-2 text-xs">
                    <span className="text-gray-500 w-12">{String(rec.type || '')}</span>
                    <span className="text-gray-300 font-mono">{String(rec.value || rec.address || '')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.open_ports.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Open Ports ({result.open_ports.length})</p>
              <div className="flex flex-wrap gap-2">
                {result.open_ports.map((port, i) => (
                  <span key={i} className="px-2 py-1 text-xs bg-amber-500/10 text-amber-400 rounded font-mono">
                    {String(port.port || '')} ({String(port.service || 'unknown')})
                  </span>
                ))}
              </div>
            </div>
          )}

          {Object.keys(result.ssl_info).length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">SSL Certificate</p>
              <div className="space-y-1 text-xs">
                {Object.entries(result.ssl_info).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="text-gray-500">{k}</span>
                    <span className="text-gray-300">{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.related_domains.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Related Domains</p>
              <div className="flex flex-wrap gap-1">
                {result.related_domains.map((d) => (
                  <span key={d} className="px-2 py-0.5 text-xs bg-surface-tertiary text-gray-400 rounded-full font-mono">
                    {d}
                  </span>
                ))}
              </div>
            </div>
          )}

          {result.vulnerabilities.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Vulnerabilities ({result.vulnerabilities.length})</p>
              <div className="space-y-1">
                {result.vulnerabilities.map((v, i) => (
                  <div key={i} className="p-2 bg-red-500/5 border border-red-500/20 rounded text-xs text-red-400">
                    {JSON.stringify(v)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.dns_records.length === 0 && result.open_ports.length === 0 && Object.keys(result.ssl_info).length === 0 && (
            <div className="text-center py-6">
              <p className="text-sm text-gray-500">Scan submitted (status: {result.status}). Results will be available once the scan completes.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
